/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import QRCode from 'qrcode.react';
import * as React from 'react';

/**
 * CheckInConfirmation component Properties.
 */
export interface ICheckInConfirmationProps {
    /**
     * The confirmation text to show.
     */
    confirmationText: string;

    /**
     * Should the order confirmation number shown as QR code or not.
     * If false, the order confirmation number (channel reference ID) will be shown as text.
     */
    shouldShowQrCode: boolean;

    /**
     * The label shows for channel reference ID.
     */
    channelReferenceIdLabel: string;

    /**
     * The channel reference ID to show. When shouldShowQrCode is true, the ID is shown as QR code, otherwise it is shown as text.
     */
    channelReferenceId?: string;
}

/**
 * CheckInConfirmation component for showing confirmation information after check-in succeed.
 * @param confirmationProps - The confirmation properties.
 * @param confirmationProps.confirmationText - The confirmation text.
 * @param confirmationProps.shouldShowQrCode - Should the order confirmation number shown as QR code or not.
 * If false, the order confirmation number (channel reference ID) will be shown as text.
 * @param confirmationProps.channelReferenceIdLabel - The label shows for channel reference ID.
 * @param confirmationProps.channelReferenceId - The channel reference ID to show.
 * When shouldShowQrCode is true, the ID is shown as QR code, otherwise it is shown as text.
 * @returns The check-in confirmation component.
 */
export const CheckInConfirmationComponent: React.FC<ICheckInConfirmationProps> = ({
    confirmationText,
    shouldShowQrCode,
    channelReferenceIdLabel,
    channelReferenceId
}) => {
    return (
        <div className='msc-check-in-for-pickup__confirmation-section col-sm-12 col-md-4 offset-md-4'>
            <h2 className='msc-check-in-for-pickup__confirmation-heading'>{confirmationText}</h2>
            {channelReferenceId && (
                <p className='msc-check-in-for-pickup__confirmation-channel-reference-id'>
                    {`${channelReferenceIdLabel} ${channelReferenceId}`}
                </p>
            )}
            {channelReferenceId && shouldShowQrCode && (
                <span className='msc-check-in-for-pickup__confirmation-qr-code'>
                    <QRCode value={channelReferenceId} size={80} />
                </span>
            )}
        </div>
    );
};
